import { useReportLegacyShellLeak } from '@upright/ui/views/unified-shell/use-report-legacy-shell-leak';
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { FooterMaybe } from '../../../../views/root/main/footer-maybe';
import { BouStatusBar, ItemsToShow } from '../../components/bou-status-bar';
import { BouColor } from '../../global/bou-colors';
import { useIsTeam } from '../../hooks';
import { themeGet } from '../../theme/custom-theme';
import { mobileOrNarrower, tabletOrNarrower } from '../breakpoint-utils';
import {
  appHeaderHeight,
  BOU_TOOL_BOTTOM_BAR_FIXED_HEIGHT,
  BOU_TOOL_PAGE_CONTENT_MIN_HEIGHT,
  SCROLLING_CONTAINER_ID,
  TITLE_BAR_HEIGHT_PX,
} from '../layout-constants';

interface Props {
  sideBarContent?: ReactNode;
  children: ReactNode;
  hideSideBar?: boolean;
  hasTitleBar?: boolean;
  /**
   * Height of the title bar to offset the content/sidebar below, when
   * `hasTitleBar` is set. Defaults to the entity TITLE_BAR_HEIGHT_PX; internal
   * pages pass INTERNAL_BREADCRUMBS_HEIGHT_PX since their breadcrumb bar is shorter.
   */
  titleBarHeight?: string;
  statusStandardItemsToShow?: ItemsToShow;
}

export function BouPageWithFrame(props: Props) {
  useReportLegacyShellLeak('BouPageWithFrame');
  const hideBottomBarOnMobile = !useIsTeam();
  const BottomBar = props.hideSideBar ? BottomBarWithoutIndent : BottomBarWithIndent;
  const titleBarHeight = props.titleBarHeight ?? TITLE_BAR_HEIGHT_PX;

  return (
    <Root hasTitleBar={props.hasTitleBar} titleBarHeight={titleBarHeight} id={SCROLLING_CONTAINER_ID}>
      <MainAndFooter sideBarHidden={props.hideSideBar}>
        <Main>{props.children}</Main>
        <FooterMaybe />
        <BottomSpacer />
      </MainAndFooter>
      {!props.hideSideBar && (
        <SideBar hasTitleBar={props.hasTitleBar} titleBarHeight={titleBarHeight}>
          {props.sideBarContent}
        </SideBar>
      )}
      <BottomBar bottomBarHiddenOnMobile={hideBottomBarOnMobile}>
        <BouStatusBar standardItemsToShow={props.statusStandardItemsToShow} />
      </BottomBar>
    </Root>
  );
}

const SideBar = styled.div<{ hasTitleBar?: boolean; titleBarHeight: string }>`
  font-size: var(--font-size__sm);
  width: 247px;
  min-width: 247px;
  max-width: 247px;
  position: fixed;
  ${props => {
    if (props.hasTitleBar) return `top: calc(${appHeaderHeight} + ${props.titleBarHeight});`;
    return `top: ${appHeaderHeight};`;
  }}
  bottom: 0;
  flex-grow: 0;
  flex-shrink: 0;
  border-right: 1px solid ${themeGet('colors.border.muted')};

  ${tabletOrNarrower(css`
    display: none;
  `)};
`;

const Root = styled.div<{ hasTitleBar?: boolean; titleBarHeight: string }>`
  font-size: var(--font-size__sm);
  position: fixed;
  ${props => {
    if (props.hasTitleBar) return `top: calc(${appHeaderHeight} + ${props.titleBarHeight});`;
    return `top: ${appHeaderHeight};`;
  }}
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  ${tabletOrNarrower(css`
    position: static;
  `)};
`;

/* For whatever reason, putting the prop-dependent fluid left-margin into one
  clause makes the margin change when clicking on a sidebar item.
  This two-line solution with !important ain't pretty, but fixes it. */
const MainAndFooter = styled.div<{ sideBarHidden?: boolean }>`
  margin-left: 247px;
  ${props => props.sideBarHidden && `margin-left: 0 !important`};
  ${tabletOrNarrower(css`
    margin-left: 0;
  `)};
`;

const Main = styled.div`
  min-height: ${BOU_TOOL_PAGE_CONTENT_MIN_HEIGHT};
`;

// Goes behind the bottom bar when the page is fully scrolled down
// so that the main content never hides there. Reserves the fixed 25px status
// bar height plus the open internal-tools-bar's measured height (published as
// `--bou-internal-tools-bar-height` on `<body>` by `BouStatusBar`, 0px when
// the bar is closed). Without this extra reservation the last content is
// hidden behind the open internal-tools-bar and cannot be scrolled into view
// (PLT-5040).
const BottomSpacer = styled.div`
  width: 100%;
  height: calc(${BOU_TOOL_BOTTOM_BAR_FIXED_HEIGHT}px + var(--bou-internal-tools-bar-height, 0px));
`;

function getBottomBarCommonStyle(bottomBarHiddenOnMobile?: boolean) {
  return css`
    position: fixed;
    z-index: 2;
    height: ${BOU_TOOL_BOTTOM_BAR_FIXED_HEIGHT}px;
    /*
    border-top: 1px solid ${BouColor.TOOL_BORDER_DARK_GRAY};
    */
    bottom: 0;
    right: 0;
    ${mobileOrNarrower(css`
      ${bottomBarHiddenOnMobile && 'display: none;'}
      margin-left: 0;
      left: 0;
    `)}
  `;
}

interface BottomBarProps {
  bottomBarHiddenOnMobile?: boolean;
}

const BottomBarWithIndent = styled.div<BottomBarProps>`
  ${props => getBottomBarCommonStyle(props.bottomBarHiddenOnMobile)}
  left: 247px;
`;

const BottomBarWithoutIndent = styled.div<BottomBarProps>`
  ${props => getBottomBarCommonStyle(props.bottomBarHiddenOnMobile)}
  left: 0;
`;
